import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Building2, Folder, Rocket, CalendarDays, User2, Loader2 } from "lucide-react";
import client from "@/api/client";

const GROUPS = [
  { key: "companies", label: "Companies", icon: Building2 },
  { key: "data_rooms", label: "Data rooms", icon: Folder },
  { key: "programs", label: "Programs", icon: Rocket },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "users", label: "Users", icon: User2 },
];

/**
 * AdminCommandPalette — ⌘K/Ctrl-K launcher for admins.
 *
 * Hits `/api/admin/search?q=...` and groups the results by entity
 * type. Selecting an item navigates to its deep-link URL. Debounced
 * (140 ms) so a fast typer doesn't spam the backend.
 *
 * Controlled from the parent so the top-bar button and the keyboard
 * shortcut can share one instance.
 */
export function AdminCommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) {
      setResults({});
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await client.get("/admin/search", { params: { q: q.trim(), limit: 5 } });
        setResults(res.data || {});
      } catch (_) {
        setResults({});
      } finally {
        setLoading(false);
      }
    }, 140);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, open]);

  // Reset input on close so re-opening is fresh.
  useEffect(() => {
    if (!open) {
      setQ("");
      setResults({});
    }
  }, [open]);

  const handleSelect = useCallback((href) => {
    onOpenChange(false);
    if (href) navigate(href);
  }, [navigate, onOpenChange]);

  const hasResults = GROUPS.some((g) => (results[g.key] || []).length > 0);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Jump to a company, data room, program, event, user…"
        value={q}
        onValueChange={setQ}
        data-testid="admin-command-input"
      />
      <CommandList data-testid="admin-command-list">
        {loading && (
          <div className="flex items-center gap-2 px-4 py-6 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
          </div>
        )}
        {!loading && !q.trim() && (
          <CommandEmpty>Type to search across the platform.</CommandEmpty>
        )}
        {!loading && q.trim() && !hasResults && (
          <CommandEmpty data-testid="admin-command-empty">No matches for “{q}”.</CommandEmpty>
        )}
        {GROUPS.map((g, i) => {
          const items = results[g.key] || [];
          if (items.length === 0) return null;
          const Icon = g.icon;
          return (
            <div key={g.key}>
              {i > 0 && <CommandSeparator />}
              <CommandGroup heading={g.label} data-testid={`admin-command-group-${g.key}`}>
                {items.map((it) => (
                  <CommandItem
                    key={`${g.key}-${it.id}`}
                    value={`${g.label} ${it.label} ${it.subtitle || ""} ${it.id}`}
                    onSelect={() => handleSelect(it.href)}
                    data-testid={`admin-command-item-${g.key}-${it.id}`}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{it.label}</p>
                      {it.subtitle && <p className="text-[11px] text-muted-foreground truncate">{it.subtitle}</p>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}

export default AdminCommandPalette;
